import { defineMutator as baseDefineMutator } from "@rocicorp/zero";
import { defineMutators, defineQueries } from "./transformer";
import { schema } from "./schema";
import { createZero, zeroDataTransformer } from "./transformer";
import { withZeroTransaction } from "./utils";

/**
 * Core Zero registry boundary.
 *
 * This file is the canonical query/mutator namespace exported to clients and
 * server adapters. Keep only stable contract wiring here.
 *
 * @see `packages/core/docs/zero.md`
 */

import { Recipe } from "../nutrition/recipe";
import { Draft } from "../common/draft";
import { FoodLog } from "../nutrition/food-log";
import { BodyMetricEntry, BodyMetricSample } from "../body/body-metric-entry";
import { BodyCharacteristic } from "../body/body-characteristic";
import { NutritionProgram } from "../nutrition/program";
import { Nutrient } from "../nutrition/nutrient";
import { Food } from "../nutrition/food";
import { FoodCategory } from "../nutrition/food-category";
import { RecipeVariation } from "../nutrition/recipe-variation";
import { Unit } from "../common/unit";
import { BodyMetric } from "../body/body-metric";
import { BodyMetricGoal } from "../body/body-metric-goal";
import { GenericFood } from "../nutrition/generic-food";
import { BrandedFood } from "../nutrition/branded-food";
import { List } from "../common/list";
import { ListCollection } from "../common/list-collection";
import { FoodCollection } from "../nutrition/food-collection";
import { MealPlan } from "../nutrition/meal-plan";
import { MealPlanningSession } from "../nutrition/meal-planning-session";
import { MealSchedule } from "../nutrition/meal-schedule";
import { PlannedMeal } from "../nutrition/planned-meal";
import { ShoppingList } from "../nutrition/shopping-list";
import { AgentSession } from "../ai/agent-session";
import { MealPlanAgent } from "../nutrition/meal-plan-agent";
import {
  MealPlanningMemberContextAgent,
  MealPlanningSessionPlannerAgent,
} from "../nutrition/meal-planning-agent";
import { Space } from "../auth/space";
import { AccessGrant } from "../auth/grant";
import { WorkflowExecution } from "../ai/workflow";

export const queries = zeroDataTransformer.defineQueries({
  units: Unit.all,
  bodyMetrics: BodyMetric.all,
  drafts: Draft.all,
  nutrient: {
    hasKey: Nutrient.hasKey,
    byKeys: Nutrient.byKeys,
  },
  food: {
    byServingId: Food.byServingId,
  },
  foodCategory: {
    all: FoodCategory.all,
  },
  foodCollection: {
    forContents: FoodCollection.forContents,
    forContentManagement: FoodCollection.forContentManagement,
    forRootManagement: FoodCollection.forRootManagement,
  },
  recipe: {
    byId: Recipe.byId,
    byIdVersion: Recipe.byIdVersion,
    listItemById: Recipe.listItemById,
    listItems: Recipe.listItems,
  },
  recipeVariation: {
    byId: RecipeVariation.byId,
    byIdVersion: RecipeVariation.byIdVersion,
  },
  foodLog: {
    onDate: FoodLog.onDate,
    forSuggestions: FoodLog.forSuggestions,
  },
  bodyCharacteristic: {
    forUser: BodyCharacteristic.forUser,
  },
  nutritionProgram: {
    inDateRange: NutritionProgram.inDateRange,
    onDate: NutritionProgram.onDate,
  },
  list: {
    all: List.all,
    details: List.details,
  },
  listCollection: {
    forContents: ListCollection.forContents,
    forContentManagement: ListCollection.forContentManagement,
    forRootManagement: ListCollection.forRootManagement,
    myDefault: ListCollection.myDefault,
  },
  bodyMetricEntry: {
    latest: BodyMetricEntry.latest,
    latestSample: BodyMetricSample.latest,
  },
  bodyMetricGoal: {
    forTypeOnDate: BodyMetricGoal.forTypeOnDate,
  },
  mealPlan: {
    byId: MealPlan.byId,
    forUser: MealPlan.forUser,
    publishedInDateRange: MealPlan.publishedInDateRange,
  },
  mealPlanningSession: {
    byId: MealPlanningSession.byId,
    list: MealPlanningSession.list,
  },
  plannedMeal: {
    byId: PlannedMeal.byId,
    eventsInDateRange: PlannedMeal.eventsInDateRange,
    withoutEventsByScope: PlannedMeal.withoutEventsByScope,
    eventAssignmentsInDateRange: PlannedMeal.eventAssignmentsInDateRange,
  },
  mealSchedule: {
    byScope: MealSchedule.byScope,
  },
  mealPlanAgentSession: {
    byMealPlanId: MealPlanAgent.sessionByMealPlanId,
    mealWorkflowsBySessionId: MealPlanAgent.mealWorkflowsBySessionId,
  },
  mealPlanningAgent: {
    plannerSession: MealPlanningSessionPlannerAgent.session,
    myMemberContextSession: MealPlanningMemberContextAgent.mySession,
    memberContextResults: MealPlanningMemberContextAgent.results,
  },
  agentSession: {
    messagesBySessionId: AgentSession.messagesBySessionId,
  },
  space: {
    byMembership: Space.byMembership,
    forManagement: Space.forManagement,
    byId: Space.byId,
    pendingInvites: Space.pendingInvites,
    memberGrants: Space.memberGrants,
  },
  accessGrant: {
    myGrants: AccessGrant.myGrants,
  },
});

export const mutators = zeroDataTransformer.defineMutators({
  genericFood: {
    create: GenericFood.create,
  },
  brandedFood: {
    create: BrandedFood.create,
  },
  foodLog: {
    createEntries: FoodLog.createEntries,
    createSourceGroup: FoodLog.createSourceGroup,
    deleteSourceGroup: FoodLog.deleteSourceGroup,
    resetSourceGroup: FoodLog.resetSourceGroup,
    editEntry: FoodLog.editEntry,
    deleteEntry: FoodLog.deleteEntry,
  },
  foodCollection: {
    createMyDefault: FoodCollection.createMyDefault,
    create: FoodCollection.create,
    rename: FoodCollection.rename,
    delete: FoodCollection.del,
    moveFood: FoodCollection.moveFood,
  },
  recipe: {
    create: Recipe.create,
    edit: Recipe.edit,
    archive: Recipe.archive,
  },
  recipeVariation: {
    createCustom: RecipeVariation.createCustom,
    editCustom: RecipeVariation.editCustom,
    archiveCustom: RecipeVariation.archiveCustom,
  },
  nutritionProgram: {
    create: NutritionProgram.create,
  },
  bodyMetricEntry: {
    createWeightOnDate: BodyMetricEntry.Weight.createOnDate,
    updateWeightSamples: BodyMetricEntry.Weight.updateSamples,
    createBodyCompositionOnDate: BodyMetricEntry.BodyComposition.createOnDate,
    updateBodyCompositionSamples: BodyMetricEntry.BodyComposition.updateSamples,
    delete: BodyMetricEntry.del,
  },
  bodyCharacteristic: {
    upsert: BodyCharacteristic.set,
  },
  bodyMetricGoal: {
    createWeight: BodyMetricGoal.Weight.create,
    cancel: BodyMetricGoal.cancel,
  },
  draft: {
    set: Draft.set,
    reset: Draft.reset,
  },
  list: {
    create: List.create,
    update: List.update,
    delete: List.del,
    addSection: List.addSection,
    updateSection: List.updateSection,
    removeSection: List.removeSection,
    addItem: List.addItem,
    updateItem: List.updateItem,
    removeItem: List.removeItem,
    updateItemStatus: List.updateItemStatus,
  },
  listCollection: {
    createMyDefault: ListCollection.createMyDefault,
    create: ListCollection.create,
    rename: ListCollection.rename,
    delete: ListCollection.del,
  },
  shoppingList: {
    create: ShoppingList.create,
  },
  mealPlan: {
    create: MealPlan.create,
    edit: MealPlan.edit,
    delete: MealPlan.del,
    publish: MealPlan.publish,
    unpublish: MealPlan.unpublish,
    addMealSlot: MealPlan.addMealSlot,
    editMealSlot: MealPlan.editMealSlot,
    removeMealSlot: MealPlan.removeMealSlot,
    setMealUserNutrientTargets: MealPlan.setMealUserNutrientTargets,
    removeMealUserNutrientTargets: MealPlan.removeMealUserNutrientTargets,
    addMeal: MealPlan.addMeal,
    editMeal: MealPlan.editMeal,
    removeMeal: MealPlan.removeMeal,
    addMealItem: MealPlan.addMealItem,
    editMealItem: MealPlan.editMealItem,
    editMealItemDescriptor: MealPlan.editMealItemDescriptor,
    removeMealItem: MealPlan.removeMealItem,
    removeMealItemDescriptor: MealPlan.removeMealItemDescriptor,
    addMealOccurrence: MealPlan.addMealOccurrence,
    editMealOccurrence: MealPlan.editMealOccurrence,
    removeMealOccurrence: MealPlan.removeMealOccurrence,
  },
  mealPlanningSession: {
    create: MealPlanningSession.create,
    createPlannedMealDrafts: MealPlanningSession.createPlannedMealDrafts,
    addMember: MealPlanningSession.addMember,
    removeMember: MealPlanningSession.removeMember,
    upsertMemberContext: MealPlanningSession.upsertMemberContext,
  },
  plannedMeal: {
    createDraft: PlannedMeal.createDraft,
    createDrafts: PlannedMeal.createDrafts,
    editDraft: PlannedMeal.editDraft,
    deleteDraft: PlannedMeal.deleteDraft,
    publish: PlannedMeal.publish,
    unpublish: PlannedMeal.unpublish,
    addPlate: PlannedMeal.addPlate,
    editPlate: PlannedMeal.editPlate,
    removePlate: PlannedMeal.removePlate,
    addEvent: PlannedMeal.addEvent,
    editEvent: PlannedMeal.editEvent,
    removeEvent: PlannedMeal.removeEvent,
    addAssignment: PlannedMeal.addAssignment,
    removeAssignment: PlannedMeal.removeAssignment,
    addPlateItem: PlannedMeal.addPlateItem,
    editPlateItem: PlannedMeal.editPlateItem,
    removePlateItem: PlannedMeal.removePlateItem,
    addPlateItemDescriptor: PlannedMeal.addPlateItemDescriptor,
    editPlateItemDescriptor: PlannedMeal.editPlateItemDescriptor,
    removePlateItemDescriptor: PlannedMeal.removePlateItemDescriptor,
  },
  agentSession: {
    create: withZeroTransaction(AgentSession.createSchema, async ({ tx, ctx, args }) => {
      await AgentSession.create({ tx, ctx, args });
    }),
    sendMessage: AgentSession.sendMessage,
    addToolCallOutput: withZeroTransaction(
      AgentSession.addToolCallOutputSchema,
      async ({ tx, ctx, args }) => {
        await AgentSession.addToolCallOutput({ tx, ctx, args });
      },
    ),
    cancelMessage: AgentSession.cancelMessage,
    cancelToolCall: AgentSession.cancelToolCall,
  },
  space: {
    create: Space.create,
    rename: Space.rename,
    archive: Space.archive,
    leave: Space.leave,
    removeMember: Space.removeMember,
    setRootGrant: Space.setRootGrant,
    setMembersGrant: Space.setMembersGrant,
  },
  workflowExecution: {
    cancel: baseDefineMutator(WorkflowExecution.cancelSchema, async () => {
      // Requires server-side implementation with registry
      throw new Error("workflowExecution.cancel requires server-side implementation");
    }),
  },
});

export type Queries = typeof queries;

export { createZero };
export { defineMutator, defineMutators, defineQueries, defineQuery } from "./transformer";

export { schema };
